import React from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Icon } from '../../../../../../../../CommonComponents/OPL/Icon/Icon';
import { InputNumberMask } from '../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Radio } from '../../../../../../../../CommonComponents/OPL/Radio/Radio';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { FormFields, getDefaultContribution, UNIT_OPTIONS } from '../../AccountForm.meta';
import { useContribution } from '../Contributions/useContribution';

interface EmployerContributionProps {
  label?: string;
}

export const EmployerContribution = ({
  label = 'Employer Contribution',
}: EmployerContributionProps) => {
  const formBag = useFormContext<FormFields>();

  const { fields } = useFieldArray<FormFields, 'bankAccount.Contributions', 'key'>({
    name: 'bankAccount.Contributions',
    keyName: 'key',
    control: formBag.control,
  });

  const accountType = useWatch({ name: 'bankAccount.AccountType', control: formBag.control });
  const watchedContributions = useWatch({
    name: 'bankAccount.Contributions',
    control: formBag.control,
  });

  const { contributionPeriodOptions } = useContribution();

  return (
    <Box>
      <Box display="flex" alignItems="center">
        <Text variant="formLabel" color="neutral.600">
          {label}
        </Text>
        <Tooltip
          content="Enter a dollar amount or a percentage (match of your contribution). Total contributions won't exceed IRS annual limits."
          placement="right-end"
        >
          <Icon className="far fa-question-circle" color="neutral.500" fontSize="small" ml="4" />
        </Tooltip>
      </Box>
      <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
        {fields.map((field, index) => {
          const contribution = watchedContributions?.[index] || getDefaultContribution(accountType);
          const { EmployerUnit: employerUnit } = contribution;

          return (
            <React.Fragment key={field.key}>
              <Box display="flex" alignItems="center">
                <Controller
                  name={`bankAccount.Contributions.${index}.EmployerFrequency`}
                  control={formBag.control}
                  render={({ field: { value, name, onChange, disabled } }) => {
                    return (
                      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                        <legend className="sr-only">Employer contribution frequency</legend>
                        {contributionPeriodOptions.map(option => (
                          <Radio
                            key={option.value}
                            name={name}
                            checked={value === option.value}
                            disabled={disabled}
                            onChange={() => {
                              onChange(option.value);
                            }}
                            mr="4"
                          >
                            {option.label}
                          </Radio>
                        ))}
                      </fieldset>
                    );
                  }}
                />
              </Box>

              <Controller
                control={formBag.control}
                name={`bankAccount.Contributions.${index}.EmployerAmount`}
                render={({ field: employerAmountField }) => (
                  <InputNumberMask
                    aria-label="Employer contribution amount"
                    {...employerAmountField}
                    allowNegative={false}
                    decimalScale={2}
                    selectorProps={{
                      defaultValue:
                        UNIT_OPTIONS.find(opt => opt.value === employerUnit) ?? UNIT_OPTIONS[0],
                      options: UNIT_OPTIONS,
                      onChange(opt) {
                        formBag.setValue(
                          `bankAccount.Contributions.${index}.EmployerUnit`,
                          opt.value,
                        );
                        formBag.setValue(`bankAccount.Contributions.${index}.EmployerAmount`, '');
                      },
                      ariaLabel: 'Employer Contribution Unit',
                    }}
                  />
                )}
              />
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};
